import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PlusCircle, TrendingUp, Target, Flame, Clock, AlertTriangle, Trophy, Coffee } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Dashboard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingFeedback, setPendingFeedback] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, sessionsRes, feedbackRes, dailyRes] = await Promise.all([
        fetch(`${API_URL}/api/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/sessions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/sessions/pending-feedback`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/users/me/daily-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (sessionsRes.ok) {
        setSessions(await sessionsRes.json());
      }
      if (feedbackRes.ok) {
        const feedbackData = await feedbackRes.json();
        if (feedbackData.has_pending) {
          setPendingFeedback(feedbackData.session);
        }
      }
      if (dailyRes.ok) {
        setDailyStats(await dailyRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get time remaining for pending sessions
  const getTimeRemaining = (targetDateUtc) => {
    if (!targetDateUtc) return null;
    const target = new Date(targetDateUtc);
    const now = new Date();
    const diff = target - now;
    
    if (diff <= 0) return 'Verificando...';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  const getResultBadge = (result, targetDateUtc) => {
    if (result === 'pending') {
      const timeLeft = getTimeRemaining(targetDateUtc);
      return (
        <div className="flex flex-col">
          <span className="inline-flex px-2 py-1 rounded-full text-xs bg-[hsl(45,93%,47%)]/20 text-[hsl(45,93%,50%)]">
            Pendiente
          </span>
          {timeLeft && (
            <span className="text-xs text-white/50 mt-1">
              Faltan {timeLeft}
            </span>
          )}
        </div>
      );
    }
    if (result === 'hit') {
      return (
        <span className="inline-flex px-2 py-1 rounded-full text-xs bg-[hsl(160,84%,39%)]/20 text-[hsl(160,84%,45%)]">
          Acierto
        </span>
      );
    }
    if (result === 'miss') {
      return (
        <span className="inline-flex px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">
          Fallo
        </span>
      );
    }
    return (
      <span className="inline-flex px-2 py-1 rounded-full text-xs bg-white/10 text-white/50">
        {result || 'N/A'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-8 animate-pulse">
        <div className="h-8 bg-white/5 rounded w-48 mb-8"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-white/5 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Pending Feedback Banner */}
      {pendingFeedback && (
        <div 
          className="mb-6 p-4 bg-[hsl(33,92%,55%)]/10 border border-[hsl(33,92%,55%)]/30 rounded-xl flex items-center justify-between"
          data-testid="pending-feedback-banner"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-[hsl(33,92%,55%)]" />
            <div>
              <p className="text-white font-medium">Tienes feedback pendiente</p>
              <p className="text-sm text-white/60">
                Debes completar el feedback de la sesión {pendingFeedback.session_code} antes de crear una nueva
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/sesion/${pendingFeedback.id}/feedback`)}
            className="px-4 py-2 bg-[hsl(33,92%,55%)] hover:bg-[hsl(33,92%,50%)] text-black font-medium rounded-lg transition-colors"
            data-testid="complete-feedback-button"
          >
            Completar Feedback
          </button>
        </div>
      )}

      {/* Stop on a Win Banner */}
      {dailyStats?.recent_hit && !pendingFeedback && (
        <div 
          className="mb-6 p-4 bg-[hsl(160,84%,39%)]/10 border border-[hsl(160,84%,39%)]/30 rounded-xl flex items-center justify-between"
          data-testid="stop-on-win-banner"
        >
          <div className="flex items-center gap-3">
            <Trophy className="w-6 h-6 text-[hsl(160,84%,39%)]" />
            <div>
              <p className="text-white font-medium">¡Felicitaciones! Tu última sesión fue un acierto 🎯</p>
              <p className="text-sm text-white/60">
                <strong className="text-[hsl(160,84%,39%)]">Consejo profesional:</strong> Considera "parar en victoria". 
                La fatiga después de un acierto puede degradar el rendimiento.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Fatigue Warning Banner */}
      {dailyStats?.fatigue_warning && !pendingFeedback && (
        <div 
          className="mb-6 p-4 bg-[hsl(252,37%,60%)]/10 border border-[hsl(252,37%,60%)]/30 rounded-xl flex items-center justify-between"
          data-testid="fatigue-warning-banner"
        >
          <div className="flex items-center gap-3">
            <Coffee className="w-6 h-6 text-[hsl(252,37%,60%)]" />
            <div>
              <p className="text-white font-medium">Atención: {dailyStats.sessions_today} sesiones hoy</p>
              <p className="text-sm text-white/60">
                Más de 2 sesiones diarias pueden causar fatiga mental y reducir la precisión. 
                Considera tomar un descanso.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Tu práctica ARV</h1>
          <p className="text-white/50 mt-1">Bienvenido, {user?.name}</p>
        </div>
        <Link
          to="/nueva-sesion"
          data-testid="nueva-sesion-cta-button"
          className="inline-flex items-center gap-2 bg-[hsl(198,62%,54%)] hover:bg-[hsl(198,62%,48%)] text-white font-medium px-5 py-3 rounded-xl transition-colors"
        >
          <PlusCircle className="w-5 h-5" />
          Nueva sesión
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <div className="kpi-card" data-testid="kpi-hit-rate-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[hsl(160,84%,39%)]/20 flex items-center justify-center">
              <Target className="w-5 h-5 text-[hsl(160,84%,50%)]" />
            </div>
            <span className="text-sm text-white/70">Tasa de aciertos</span>
          </div>
          <p className="text-3xl font-bold text-white" data-testid="hit-rate-value">
            {stats?.hit_rate || 0}%
          </p>
          <p className="text-xs text-white/40 mt-1">
            {stats?.hits || 0} de {stats?.total_sessions || 0} sesiones
          </p>
        </div>

        <div className="kpi-card" data-testid="kpi-zscore-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[hsl(252,37%,60%)]/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-[hsl(252,37%,70%)]" />
            </div>
            <span className="text-sm text-white/70">Z-score</span>
          </div>
          <p className="text-3xl font-bold text-white" data-testid="zscore-value">
            {stats?.z_score > 0 ? '+' : ''}{stats?.z_score || 0}
          </p>
          <p className="text-xs text-white/40 mt-1">
            {stats?.z_score >= 2 ? 'Estadísticamente significativo' : 'Sigue practicando'}
          </p>
        </div>

        <div className="kpi-card" data-testid="kpi-streak-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[hsl(33,92%,55%)]/20 flex items-center justify-center">
              <Flame className="w-5 h-5 text-[hsl(33,92%,60%)]" />
            </div>
            <span className="text-sm text-white/70">Racha actual</span>
          </div>
          <p className="text-3xl font-bold text-white" data-testid="streak-value">
            {stats?.current_streak || 0}
          </p>
          <p className="text-xs text-white/40 mt-1">
            Mejor racha: {stats?.best_streak || 0}
          </p>
        </div>

        <div className="kpi-card" data-testid="kpi-total-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[hsl(198,62%,54%)]/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-[hsl(198,62%,60%)]" />
            </div>
            <span className="text-sm text-white/70">Total sesiones</span>
          </div>
          <p className="text-3xl font-bold text-white" data-testid="total-value">
            {stats?.total_sessions || 0}
          </p>
          <p className="text-xs text-white/40 mt-1">
            Sesiones completadas
          </p>
        </div>
      </div>

      {/* Recent Sessions */}
      <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Sesiones recientes</h2>
        
        {sessions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-white/50 mb-4">Aún no hay sesiones. Inicia tu primera práctica ARV.</p>
            <Link
              to="/nueva-sesion"
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <PlusCircle className="w-4 h-4" />
              Crear sesión
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="sessions-table">
              <thead>
                <tr className="text-left text-sm text-white/50 border-b border-[hsl(232,18%,18%)]">
                  <th className="pb-3 font-medium">Sesión</th>
                  <th className="pb-3 font-medium">Fecha</th>
                  <th className="pb-3 font-medium">Estado</th>
                  <th className="pb-3 font-medium">Confianza</th>
                </tr>
              </thead>
              <tbody>
                {sessions.slice(0, 10).map((session) => (
                  <tr 
                    key={session.id} 
                    className="border-b border-[hsl(232,18%,18%)] hover:bg-white/5 transition-colors"
                    data-testid="session-row"
                  >
                    <td className="py-4">
                      <Link 
                        to={`/sesion/${session.id}`}
                        className="text-[hsl(198,62%,54%)] hover:underline"
                      >
                        {session.session_code}
                      </Link>
                    </td>
                    <td className="py-4 text-white/70">
                      {new Date(session.created_at).toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="py-4">
                      {getResultBadge(session.result, session.target_date_utc)}
                    </td>
                    <td className="py-4 text-white/70">
                      {session.confidence ? `${session.confidence}/5` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
